import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  DataStatus,
  Separator,
  showNotification,
  Switch,
} from 'platform/components';
import {Center, Heading, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useRef, useState} from 'react';

import {isNil} from 'ramda';

import i18n from '@dms/i18n';

import {useOnMount} from 'shared/web';

import type {
  GeneratedReportDocument,
  ReportDocumentPreferences,
  ReportDocumentSectionOption,
  ReportDocumentSource,
} from './ChartReportDocument';
import {
  downloadReportDocument,
  generateReportDocument,
  openReportDocument,
} from './generateReportDocument';

type GenerationState =
  | {status: 'selecting'}
  | {status: 'generating'}
  | {document: GeneratedReportDocument; status: 'ready'}
  | {status: 'error'};

export interface ReportDocumentDialogProps<TSectionId extends string> {
  createDocument: (preferences: ReportDocumentPreferences<TSectionId>) => ReportDocumentSource;
  onClose: VoidFunction;
  reportName: string;
  sectionOptions: ReadonlyArray<ReportDocumentSectionOption<TSectionId>>;
  selectionDescription?: string;
  /** Fixed reports always include every section and the document context. */
  isContentFixed?: boolean;
}

export function ReportDocumentDialog<TSectionId extends string>(
  props: ReportDocumentDialogProps<TSectionId>
) {
  const sectionIds = props.sectionOptions.map((option) => option.id);
  const [selectedSectionIds, setSelectedSectionIds] = useState<TSectionId[]>(sectionIds);
  const [includeContext, setIncludeContext] = useState(true);
  const [generationState, setGenerationState] = useState<GenerationState>({status: 'selecting'});
  const generationAbortController = useRef<AbortController | null>(null);
  const generationRefreshTimeout = useRef<number | undefined>(undefined);
  const isAllSelected = selectedSectionIds.length === sectionIds.length;
  const includedSections = props.sectionOptions.filter((option) =>
    selectedSectionIds.includes(option.id)
  );

  const clearRefreshTimeout = () => {
    if (!isNil(generationRefreshTimeout.current)) {
      window.clearTimeout(generationRefreshTimeout.current);
      generationRefreshTimeout.current = undefined;
    }
  };

  useOnMount(undefined, () => {
    generationAbortController.current?.abort();
    clearRefreshTimeout();
  });

  const handleSectionChange = (id: TSectionId, isSelected: boolean) => {
    setSelectedSectionIds((current) => {
      const selected = new Set(current);
      if (isSelected) {
        selected.add(id);
      } else {
        selected.delete(id);
      }
      return sectionIds.filter((sectionId) => selected.has(sectionId));
    });
  };

  const handleSelectAll = () => {
    setSelectedSectionIds(isAllSelected ? [] : [...sectionIds]);
  };

  const handleGenerate = (isRefresh = false) => {
    clearRefreshTimeout();
    generationAbortController.current?.abort();
    const abortController = new AbortController();
    generationAbortController.current = abortController;
    if (!isRefresh) {
      setGenerationState({status: 'generating'});
    }
    const preferences: ReportDocumentPreferences<TSectionId> = {
      includeContext: props.isContentFixed ? true : includeContext,
      selectedSectionIds: props.isContentFixed ? sectionIds : selectedSectionIds,
    };

    let source: ReportDocumentSource;
    try {
      source = props.createDocument(preferences);
    } catch {
      if (!isRefresh) {
        setGenerationState({status: 'error'});
      }
      return;
    }

    void generateReportDocument(source, {signal: abortController.signal})
      .then((document) => {
        if (abortController.signal.aborted) {
          return;
        }
        setGenerationState({status: 'ready', document});
        if (document.kind === 'pdf' && !isNil(document.refreshAfterMs)) {
          generationRefreshTimeout.current = window.setTimeout(
            () => handleGenerate(true),
            document.refreshAfterMs
          );
        }
        if (isRefresh) {
          return;
        }
        if (document.kind === 'pdf') {
          showNotification.success(i18n.t('module.reportDocument.notifications.generated'));
        } else {
          showNotification.warning(
            i18n.t('module.reportDocument.notifications.printFallbackGenerated')
          );
        }
      })
      .catch(() => {
        if (!abortController.signal.aborted) {
          setGenerationState({status: 'error'});
        }
      });
  };

  const handleChangeSections = () => {
    clearRefreshTimeout();
    generationAbortController.current?.abort();
    generationAbortController.current = null;
    setGenerationState({status: 'selecting'});
  };

  const selectionContent = (
    <VStack spacing={4}>
      <Show when={Boolean(props.selectionDescription)}>
        <Text size="small" color="secondary">
          {props.selectionDescription}
        </Text>
      </Show>

      <HStack justify="space-between" align="center">
        <Text size="xSmall" color="secondary" alternative>
          {props.isContentFixed
            ? i18n.t('module.reportDocument.labels.includedSections').toUpperCase()
            : `${i18n.t('module.reportDocument.labels.sections').toUpperCase()} · ${i18n.t(
                'module.reportDocument.labels.selectedSectionCount',
                {selected: selectedSectionIds.length, total: sectionIds.length}
              )}`}
        </Text>
        <Show when={!props.isContentFixed}>
          <Button
            variant="link"
            size="small"
            leftIcon={isAllSelected ? 'navigation/close' : 'navigation/check'}
            title={i18n.t(
              isAllSelected ? 'general.actions.unselectAll' : 'general.actions.selectAll'
            )}
            onClick={handleSelectAll}
          />
        </Show>
      </HStack>

      <Card variant="inlineGrey">
        <VStack spacing={3}>
          {props.sectionOptions.map((option, index) => (
            <VStack key={option.id} spacing={3}>
              <Switch
                value={props.isContentFixed ? true : selectedSectionIds.includes(option.id)}
                isDisabled={props.isContentFixed}
                label={option.title}
                helperText={option.description}
                onChange={(value) => handleSectionChange(option.id, value)}
                data-testid={`report-section-${option.id}`}
              />
              <Show when={index < props.sectionOptions.length - 1}>
                <Separator spacing={0} />
              </Show>
            </VStack>
          ))}
        </VStack>
      </Card>

      <Show when={!props.isContentFixed}>
        <VStack spacing={2}>
          <Text size="xSmall" color="secondary" alternative>
            {i18n.t('general.labels.document').toUpperCase()}
          </Text>
          <Switch
            value={includeContext}
            label={i18n.t('module.reportDocument.labels.includeContext')}
            helperText={i18n.t('module.reportDocument.labels.includeContextDescription')}
            onChange={setIncludeContext}
            data-testid="report-include-context"
          />
        </VStack>
      </Show>

      <Separator spacing={0} />
      <ButtonGroup
        align="right"
        buttons={[
          {title: i18n.t('general.actions.discard'), variant: 'secondary', onClick: props.onClose},
          {
            title: i18n.t('entity.order.actions.generate'),
            isDisabled: selectedSectionIds.length === 0,
            onClick: () => handleGenerate(),
          },
        ]}
      />
    </VStack>
  );

  return match(generationState)
    .with({status: 'selecting'}, () => selectionContent)
    .with({status: 'generating'}, () => <DataStatus isLoading minHeight={50} />)
    .with({status: 'ready'}, ({document}) => (
      <VStack spacing={4}>
        <Alert
          type="inline"
          variant={document.kind === 'pdf' ? 'success' : 'warning'}
          message={
            document.kind === 'pdf'
              ? i18n.t('module.reportDocument.labels.ready')
              : i18n.t('module.reportDocument.labels.printFallbackReady')
          }
        />

        <Card variant="inlineGrey">
          <HStack spacing={3} align="center" justify="space-between" wrap>
            <HStack spacing={3} align="center">
              {/*
                The file icon is 36 × 40 px, but Icon renders a 40 × 40 px box, so Center keeps
                a 36 px column. isNotFilled keeps the icon colors: the files_default exclusion in
                Icon does not match, because Tailwind turns its underscore into a space.
              */}
              <Center width={9}>
                <Icon
                  value={
                    document.kind === 'pdf'
                      ? 'files_default/file_type_PDF'
                      : 'files_default/file_type_UNKNOWN'
                  }
                  size={10}
                  isNotFilled
                />
              </Center>
              <VStack spacing={1}>
                <Heading size={4}>{document.filename}</Heading>
                <Text size="xSmall" color="secondary">
                  {props.reportName} · {selectedSectionIds.length}{' '}
                  {i18n.t('module.reportDocument.labels.sections').toLowerCase()}
                </Text>
              </VStack>
            </HStack>
            <ButtonGroup>
              <Button
                variant="link"
                title={
                  document.kind === 'pdf'
                    ? i18n.t('general.actions.open')
                    : i18n.t('general.actions.printPDF')
                }
                onClick={() => openReportDocument(document)}
              />
              <Button
                variant="secondary"
                leftIcon="file/download"
                title={
                  document.kind === 'pdf'
                    ? i18n.t('general.actions.download')
                    : i18n.t('module.reportDocument.actions.downloadHtml')
                }
                onClick={() => downloadReportDocument(document)}
              />
            </ButtonGroup>
          </HStack>
        </Card>

        <Card
          variant="inlineGrey"
          title={i18n.t('module.reportDocument.labels.includedSections')}
          hasSeparator
          actions={
            props.isContentFixed
              ? undefined
              : [
                  {
                    type: 'button',
                    variant: 'link',
                    size: 'small',
                    leftIcon: 'navigation/refresh',
                    title: i18n.t('module.reportDocument.actions.changeSections'),
                    onClick: handleChangeSections,
                  },
                ]
          }
        >
          <VStack spacing={2}>
            {includedSections.map((option, index) => (
              <VStack key={option.id} spacing={2}>
                <HStack spacing={4} align="center">
                  <Icon value="action/check_circle" size={4} color="severity.success" />
                  <Text size="small" alternative>
                    {option.title}
                  </Text>
                </HStack>
                <Show when={index < includedSections.length - 1}>
                  <Separator spacing={0} />
                </Show>
              </VStack>
            ))}
          </VStack>
        </Card>

        <ButtonGroup
          align="right"
          buttons={[
            {title: i18n.t('general.actions.close'), variant: 'secondary', onClick: props.onClose},
          ]}
        />
      </VStack>
    ))
    .with({status: 'error'}, () => (
      <VStack spacing={4}>
        <Alert
          type="inline"
          variant="error"
          message={i18n.t('general.labels.somethingWentWrong')}
        />
        <ButtonGroup
          align="right"
          buttons={[
            {title: i18n.t('general.actions.close'), variant: 'secondary', onClick: props.onClose},
            {title: i18n.t('general.actions.retry'), onClick: () => handleGenerate()},
          ]}
        />
      </VStack>
    ))
    .exhaustive();
}
